import { useState, useEffect, useRef } from 'react';
import { Camera, Palette, ArrowLeft, EyeClosed } from 'lucide-react';
import { CameraColorPicker } from './CameraColorPicker';
import { ColorPicker } from './ColorPicker';

interface CaptureModeSelectorProps {
  onComplete: (colors: string[]) => void;
  onCancel: (buttonRect?: DOMRect) => void;
  initialColors?: string[];
  startInCameraMode?: boolean;
}

type CaptureMode = 'select' | 'camera' | 'manual';
type TransitionPhase = 'idle' | 'moving' | 'expanding' | 'blinking' | 'fading' | 'complete';
type CameraTransitionPhase = 'idle' | 'ready' | 'closing' | 'closed' | 'opening';

export function CaptureModeSelector({ onComplete, onCancel, initialColors, startInCameraMode }: CaptureModeSelectorProps) {
  // Determine initial mode: camera if requested, manual if remix colors, otherwise select
  const [mode, setMode] = useState<CaptureMode>(
    startInCameraMode ? 'camera' : (initialColors?.length ? 'manual' : 'select')
  );
  const [hasCamera, setHasCamera] = useState<boolean | null>(null);
  const [transitionPhase, setTransitionPhase] = useState<TransitionPhase>('idle');
  // If starting in camera mode, begin with shutter closed so it can open
  const [cameraTransition, setCameraTransition] = useState<CameraTransitionPhase>(
    startInCameraMode ? 'closed' : 'idle'
  );
  const [iconPosition, setIconPosition] = useState({ x: 0, y: 0 });
  const [expandScale, setExpandScale] = useState(1);
  const [uiOpacity, setUiOpacity] = useState(0);
  const [hideTransitionCircle, setHideTransitionCircle] = useState(false);
  const paletteButtonRef = useRef<HTMLButtonElement>(null);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const animationRef = useRef<number | null>(null);
  const cameraTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const checkCameraCapability = async () => {
      try {
        // Check if mediaDevices API and getUserMedia are available
        if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
          setHasCamera(false);
          return;
        }

        // Check the permissions API to see if camera access has been denied
        // This doesn't prompt the user, just checks the current permission state
        if (navigator.permissions && typeof navigator.permissions.query === 'function') {
          try {
            const permissionStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });
            if (permissionStatus.state === 'denied') {
              // User has previously denied camera access
              setHasCamera(false);
              return;
            }
          } catch {
            // permissions.query failed (e.g., camera not supported in permissions API)
            // Continue with device enumeration check
          }
        }

        // Check if enumerateDevices is available
        if (typeof navigator.mediaDevices.enumerateDevices !== 'function') {
          // If we can't enumerate, assume camera might be available if getUserMedia exists
          // The actual permission check will happen when user clicks the button
          setHasCamera(true);
          return;
        }

        // Check if any video input devices exist (doesn't require permission)
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter((d) => d.kind === 'videoinput');
        
        // If no video devices found, camera is not available
        setHasCamera(videoDevices.length > 0);
      } catch {
        // enumerateDevices failed - camera likely not available
        setHasCamera(false);
      }
    };

    checkCameraCapability();
  }, []);

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (cameraTimeoutRef.current) {
        clearTimeout(cameraTimeoutRef.current);
      }
    };
  }, []);

  // Handle camera shutter transition
  const handleCameraClick = () => {
    // First render bars in open position (off-screen)
    setCameraTransition('ready');
    
    // Then start closing after a frame
    cameraTimeoutRef.current = window.setTimeout(() => {
      setCameraTransition('closing');
      
      // After shutter closes, switch to closed state briefly
      cameraTimeoutRef.current = window.setTimeout(() => {
        setCameraTransition('closed');
        
        // Give camera time to load, then switch to camera mode
        // CameraColorPicker will handle opening the shutter
        cameraTimeoutRef.current = window.setTimeout(() => {
          setMode('camera');
        }, 600);
      }, 200);
    }, 20);
  };

  const handlePickColors = () => {
    if (!paletteButtonRef.current) {
      setMode('manual');
      return;
    }

    const rect = paletteButtonRef.current.getBoundingClientRect();
    const iconCenterX = rect.left + 28 + 28; // Button padding + icon radius
    const iconCenterY = rect.top + rect.height / 2;
    
    // Calculate center of screen (where color wheel will be)
    const screenCenterX = window.innerWidth / 2;
    const screenCenterY = window.innerHeight / 2 - 20; // Slightly above center
    
    // Starting position relative to where it will end up (center)
    const startX = iconCenterX - screenCenterX;
    const startY = iconCenterY - screenCenterY;
    
    setIconPosition({ x: startX, y: startY });
    setTransitionPhase('moving');
    
    // Phase 1: Move icon to center
    const moveDuration = 400;
    const moveStartTime = performance.now();
    
    const animateMove = (currentTime: number) => {
      const elapsed = currentTime - moveStartTime;
      const progress = Math.min(elapsed / moveDuration, 1);
      
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      
      setIconPosition({
        x: startX * (1 - eased),
        y: startY * (1 - eased),
      });
      
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animateMove);
      } else {
        // Start expand phase
        setTransitionPhase('expanding');
        startExpandPhase();
      }
    };
    
    animationRef.current = requestAnimationFrame(animateMove);
  };

  const startExpandPhase = () => {
    const expandDuration = 500;
    const expandStartTime = performance.now();
    const targetScale = 280 / 56; // Target size (280px wheel) / icon size (56px)
    
    const animateExpand = (currentTime: number) => {
      const elapsed = currentTime - expandStartTime;
      const progress = Math.min(elapsed / expandDuration, 1);
      
      // Ease out quad
      const eased = 1 - Math.pow(1 - progress, 2);
      
      setExpandScale(1 + (targetScale - 1) * eased);
      
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animateExpand);
      } else {
        // Start blink phase before fading
        setTransitionPhase('blinking');
        // Hide the transition circle when eyelid reaches bottom (at 250ms, halfway through 500ms blink)
        // This reveals the color picker underneath as the eyelid opens back up
        cameraTimeoutRef.current = window.setTimeout(() => {
          setHideTransitionCircle(true);
        }, 250);
        // Wait for blink animation to complete (500ms)
        window.setTimeout(() => {
          // Start fade in UI phase
          setTransitionPhase('fading');
          startFadePhase();
        }, 500);
      }
    };
    
    animationRef.current = requestAnimationFrame(animateExpand);
  };

  const startFadePhase = () => {
    const fadeDuration = 300;
    const fadeStartTime = performance.now();
    
    const animateFade = (currentTime: number) => {
      const elapsed = currentTime - fadeStartTime;
      const progress = Math.min(elapsed / fadeDuration, 1);
      
      // Linear fade
      setUiOpacity(progress);
      
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animateFade);
      } else {
        setTransitionPhase('complete');
        setMode('manual');
      }
    };
    
    animationRef.current = requestAnimationFrame(animateFade);
  };

  const resetToSelect = () => {
    // Reset all transition state when going back
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    if (cameraTimeoutRef.current) {
      clearTimeout(cameraTimeoutRef.current);
    }
    setMode('select');
    setTransitionPhase('idle');
    setCameraTransition('idle');
    setIconPosition({ x: 0, y: 0 });
    setExpandScale(1);
    setUiOpacity(0);
    setHideTransitionCircle(false);
  };

  if (mode === 'camera') {
    return (
      <CameraColorPicker
        onComplete={onComplete}
        onCancel={(_rect) => {
          // When camera is cancelled (e.g., permission denied), go back to select mode
          // instead of fully cancelling the capture flow
          resetToSelect();
        }}
        showOpeningShutter={cameraTransition !== 'idle'}
      />
    );
  }

  if (mode === 'manual') {
    return (
      <ColorPicker
        onComplete={onComplete}
        onCancel={onCancel}
        initialColors={initialColors}
      />
    );
  }

  // Show transition animation
  if (transitionPhase !== 'idle') {
    // Calculate the size of the expanded circle
    const expandedSize = 56 * expandScale;
    
    return (
      <div className="fixed inset-0 z-50 bg-gradient-to-br from-rose-200 via-amber-100 via-emerald-100 to-sky-200 dark:from-rose-950 dark:via-violet-950 dark:to-sky-950 flex flex-col">
        {/* Centered animated palette icon */}
        <div className="flex-1 flex items-center justify-center">
          {/* The gradient circle with palette icon - hidden once eyelid reaches bottom */}
          {!hideTransitionCircle && (
            <div 
              className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-400 to-sky-400 flex items-center justify-center shadow-xl overflow-hidden relative"
              style={{
                transform: `translate(${iconPosition.x}px, ${iconPosition.y}px) scale(${expandScale})`,
                transition: 'none',
              }}
            >
              <Palette 
                className="text-white" 
                style={{
                  width: 24,
                  height: 24,
                }}
              />
            </div>
          )}
          
          {/* Eyelid blink animation - positioned separately so it persists */}
          {transitionPhase === 'blinking' && (
            <div 
              className="absolute rounded-full pointer-events-none overflow-hidden"
              style={{
                width: expandedSize,
                height: expandedSize,
              }}
            >
              <div
                className="absolute left-0 right-0 animate-eyelid-blink"
                style={{
                  top: 0,
                  width: '100%',
                  height: '100%',
                }}
              >
                {/* The circular eyelid */}
                <div
                  className="absolute rounded-full"
                  style={{
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(to bottom right, #34d399, #38bdf8)',
                    bottom: 0,
                    left: 0,
                  }}
                />
                {/* EyeClosed icon on the eyelid */}
                <EyeClosed 
                  className="absolute w-full h-auto"
                  style={{ 
                    color: '#059669',
                    top: '100%',
                    transform: 'translateY(-60%)',
                  }}
                  strokeWidth={1.5}
                />
              </div>
            </div>
          )}
        </div>
        
        {/* Fading in ColorPicker UI overlay */}
        {transitionPhase === 'fading' && (
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{ opacity: uiOpacity }}
          >
            <ColorPicker
              onComplete={onComplete}
              onCancel={() => setMode('select')}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-rose-200 via-amber-100 via-emerald-100 to-sky-200 dark:from-rose-950 dark:via-violet-950 dark:to-sky-950 flex flex-col">
      {/* Header */}
      <div className="max-w-xl mx-auto w-full px-5 pb-4 safe-top-mobile" style={{ '--safe-top-min': '1.5rem' } as React.CSSProperties}>
        <button
          ref={backButtonRef}
          onClick={() => {
            const rect = backButtonRef.current?.getBoundingClientRect();
            onCancel(rect);
          }}
          className="p-2 -ml-2 rounded-full text-foreground/60 hover:text-foreground hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-6">
        
        {/* Eye icon */}
        <div className="relative w-24 h-24 mb-8">
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-rose-300 via-amber-200 to-sky-300 animate-pulse" />
          <div className="absolute inset-3 rounded-full bg-white/80 dark:bg-black/30" />
          <div className="absolute inset-0 flex items-center justify-center text-4xl">
            👁️
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-10">
          <h2 className="text-2xl font-semibold mb-2">What do you see?</h2>
          <p className="text-muted-foreground">
            Capture colors from the world
          </p>
        </div>

        {/* Options */}
        <div className="w-full max-w-sm space-y-3">
          {/* Camera */}
          <button
            onClick={handleCameraClick}
            disabled={hasCamera === false || cameraTransition !== 'idle'}
            className="w-full p-4 rounded-2xl bg-white/60 dark:bg-white/10 hover:bg-white/80 dark:hover:bg-white/20 transition-all disabled:opacity-50 disabled:pointer-events-none disabled:grayscale flex items-center gap-4"
          >
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-rose-400 to-amber-400 flex items-center justify-center">
              <Camera className="h-6 w-6 text-white" />
            </div>
            <div className="text-left">
              <div className="font-medium text-lg">Use camera</div>
              <div className="text-sm text-muted-foreground">
                {hasCamera === false ? 'No camera available' : 'Point at what you see'}
              </div>
            </div>
          </button>

          {/* Manual - Pick colors */}
          <button
            ref={paletteButtonRef}
            onClick={handlePickColors}
            className="w-full p-4 rounded-2xl bg-white/60 dark:bg-white/10 hover:bg-white/80 dark:hover:bg-white/20 transition-all flex items-center gap-4"
          >
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-400 to-sky-400 flex items-center justify-center">
              <Palette className="h-6 w-6 text-white" />
            </div>
            <div className="text-left">
              <div className="font-medium text-lg">Pick colors</div>
              <div className="text-sm text-muted-foreground">Choose from memory</div>
            </div>
          </button>
        </div>
      </div>

      {/* Camera shutter overlay - two bars blinking shut */}
      {cameraTransition !== 'idle' && (
        <div className="fixed inset-0 z-[60] pointer-events-none overflow-hidden">
          {/* Top bar - use calc(50% + 2px) to ensure overlap at center, matching the opening animation */}
          <div 
            className="absolute top-0 left-0 right-0 bg-gradient-to-b from-zinc-700 to-zinc-800 overflow-hidden"
            style={{
              height: 'calc(50dvh + 2px)',
              transform: cameraTransition === 'closing' || cameraTransition === 'closed' 
                ? 'translateY(0)' 
                : 'translateY(-100%)',
              transition: cameraTransition === 'ready' 
                ? 'none' 
                : `transform ${cameraTransition === 'closing' ? '200ms' : '250ms'} ${cameraTransition === 'closing' ? 'cubic-bezier(0.4, 0, 0.2, 1)' : 'cubic-bezier(0, 0, 0.2, 1)'}`,
            }}
          >
            {/* Ridged texture lines */}
            <div className="absolute inset-0 opacity-20 flex flex-col justify-evenly py-8">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-px bg-zinc-400 w-full" />
              ))}
            </div>
            {/* Bottom edge highlight */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-b from-zinc-600 to-zinc-900" />
          </div>
          
          {/* Bottom bar - use calc(50% + 2px) to ensure overlap at center, matching the opening animation */}
          <div 
            className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-zinc-700 to-zinc-800 overflow-hidden"
            style={{
              height: 'calc(50dvh + 2px)',
              transform: cameraTransition === 'closing' || cameraTransition === 'closed' 
                ? 'translateY(0)' 
                : 'translateY(100%)',
              transition: cameraTransition === 'ready' 
                ? 'none' 
                : `transform ${cameraTransition === 'closing' ? '200ms' : '250ms'} ${cameraTransition === 'closing' ? 'cubic-bezier(0.4, 0, 0.2, 1)' : 'cubic-bezier(0, 0, 0.2, 1)'}`,
            }}
          >
            {/* Ridged texture lines */}
            <div className="absolute inset-0 opacity-20 flex flex-col justify-evenly py-8">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-px bg-zinc-400 w-full" />
              ))}
            </div>
            {/* Top edge highlight */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-t from-zinc-600 to-zinc-900" />
          </div>

          {/* Center gradient eye when closed */}
          <div 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-all duration-200"
            style={{
              opacity: cameraTransition === 'closed' ? 1 : 0,
              transform: `translate(-50%, -50%) scale(${cameraTransition === 'closed' ? 1 : 0.5})`,
            }}
          >
            <div className="relative w-20 h-20">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-rose-300 via-amber-200 to-sky-300 animate-pulse" />
              <div className="absolute inset-2 rounded-full bg-white/80 dark:bg-black/30" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-4 h-4 rounded-full bg-zinc-800" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
